import { Grid } from '@mantine/core';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';

const initStripo = (
  accountId,
  domainId,
  fetch,
  mergeTags,
  productFeeds,
  templates,
  surveys,
  design,
  externalId,
  onStripoLoad
) => {
  if (!accountId || !domainId || !externalId || !mergeTags) {
    return;
  }

  window.Stripo.init({
    settingsId: 'stripoSettingsContainer',
    previewId: 'stripoPreviewContainer',
    locale: 'en',
    unSubscribeLink: '{{unsubscribeLink}}',
    socialNetworks: [],
    extensions: [
      {
        globalName: 'TemplatedProductBlockExtension',
        url: `${import.meta.env.VITE_ROOT}/stripo-extensions/product-block-templated/main.extension.js`,
      },
      {
        globalName: 'GenerationAreaExtension',
        url: `${import.meta.env.VITE_ROOT}/stripo-extensions/generation-area/main.extension.js`,
      },
    ],
    relevaProductBlock: {
      enabled: true,
      productFeeds: productFeeds.map((f) => ({ id: f.id, name: f.name })),
      templates: templates.map((f) => ({ id: f.id, name: f.name })),
    },
    mergeTags: [
      {
        category: 'Releva',
        entries: mergeTags.map((t) => ({
          label: t.label,
          value: t.pattern,
        })),
      },
    ],
    specialLinks: [
      {
        category: 'Personalized links',
        entries: [
          {
            label: 'Unsubscribe link',
            value: '{{unsubscribeLink}}',
          },
          {
            label: 'Loyalty prgoram link',
            value: '{{loyaltyLink}}',
          },
          {
            label: 'Subscribe link',
            value: '{{subscribeLink}}',
          },
          {
            label: 'Subscription Management Link',
            value: '{{subscriptionManagementLink}}',
          },

          {
            label: 'View in browser link',
            value: '{{viewInBrowserLink}}',
          },
          ...surveys.map((survey) => {
            return {
              label: `Survey link - ${survey.name}`,
              value: `{{surveyLink '${survey.token}'}}`,
            };
          }),
        ],
      },
    ],
    customAppearanceMergetagsInLinks: 'true',
    editorFonts: {
      showDefaultStandardFonts: true,
      showDefaultNotStandardFonts: true,
      customFonts: [
        {
          name: 'Barlow',
          fontFamily: "'Barlow', sans-serif",
          url: 'https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,400;0,800;1,400;1,800&display=swap',
        },
        {
          name: 'Exo 2',
          fontFamily: "'Exo 2', sans-serif",
          url: 'https://fonts.googleapis.com/css2?family=Exo+2:ital,wght@0,400;0,800;1,400;1,800&display=swap',
        },
        {
          name: 'Oswald',
          fontFamily: "'Oswald', 'helvetica neue', helvetica, arial, sans-serif",
          url: 'https://fonts.googleapis.com/css?family=Oswald',
        },
        {
          name: 'Barriecito',
          fontFamily: "'Barriecito', cursive",
          url: 'https://fonts.googleapis.com/css?family=Barriecito&display=swap',
        },
        {
          name: 'Raleway',
          fontFamily: "'Raleway', sans-serif",
          url: 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,800;1,400;1,800&display=swap',
        },
        {
          name: 'Montserrat',
          fontFamily: "'Montserrat', sans-serif",
          url: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,800;1,400;1,800&display=swap',
        },
      ],
    },
    html: JSON.parse(design).html,
    css: JSON.parse(design).css,
    apiBaseUrl: 'https://plugins.stripo.email/api/v1',
    apiRequestData: {
      emailId: externalId,
      userId: domainId,
    },
    codeEditorButtonId: 'codeEditor',
    getAuthToken: (callback) => {
      fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails/stripo/auth`, {
        method: 'POST',
      })
        .then((body) => callback(body.token))
        .catch(console.error);
    },
    canBeSavedToLibrary: () => {
      return true;
    },
    onTemplateLoaded: () => {
      onStripoLoad();
    },
  });
};

const StripoEditor = ({
  mergeTags,
  productFeeds,
  templates,
  surveys,
  design,
  externalId,
  stripoEditorRef,
  onStripoLoad,
}) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();

  useEffect(() => {
    // Initialize Stripo once per email (keyed on externalId). The editor is kept
    // mounted and rendered for the whole session — EmailFormInner hides it
    // off-screen (never display:none) when the Advanced tab is active — so we
    // never need to re-seed it: re-seeding a live editor would discard in-editor
    // edits. The seed `design` must be correct on first init; for the
    // gallery-template flow EmailsNewForm defers creating the email until the
    // template has loaded.
    initStripo(
      accountId,
      domainId,
      fetch,
      mergeTags,
      productFeeds,
      templates,
      surveys,
      design,
      externalId,
      onStripoLoad
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalId]);

  useEffect(() => {
    if (!stripoEditorRef.current) {
      stripoEditorRef.current = window.StripoApi;
    }
  }, [stripoEditorRef]);

  return (
    <Grid>
      <Grid.Col span={{ base: 12, sm: 8 }} id="stripoPreviewContainer"></Grid.Col>
      <Grid.Col span={{ base: 12, sm: 4 }} id="stripoSettingsContainer"></Grid.Col>
    </Grid>
  );
};

export default StripoEditor;
